import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import { Siren, X, User, Users, Video, Phone, Loader2 } from 'lucide-react';
import { Button } from '../components';
import { fetchMedewerkerCrisisStatus, maakMedewerkerCrisisAlarm } from '../services/crisisApi';

function foutMelding(error: unknown, fallback: string): string {
  if (isAxiosError(error)) {
    const msg = (error.response?.data as { message?: string } | undefined)?.message;
    if (msg) return msg;
  }
  return fallback;
}

/**
 * 🚨 Rode alarmknop in het medewerkersportaal. Alleen zichtbaar als de werkgever
 * de 24/7 Landelijke Crisisdienst voor minstens één vestiging heeft geactiveerd
 * (wizard in de cockpit). Een medewerkersalarm verschijnt bij de werkgever onder
 * het pseudoniem; tegoed- en factuurgegevens blijven voor de medewerker verborgen.
 */
export const MedewerkerCrisisKnop: React.FC = () => {
  const { t } = useTranslation('medewerker');
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [locatieId, setLocatieId] = useState<string | null>(null);
  const [type, setType] = useState<'SINGLE' | 'DUAL'>('SINGLE');
  const [omschrijving, setOmschrijving] = useState('');

  const statusQuery = useQuery({
    queryKey: ['medewerker-crisis'],
    queryFn: fetchMedewerkerCrisisStatus,
    refetchInterval: (q) => (q.state.data?.mijn_alarmen.length ? 10000 : false),
    retry: false,
  });
  const status = statusQuery.data;

  const alarmMutation = useMutation({
    mutationFn: () => maakMedewerkerCrisisAlarm({ location_id: locatieId ?? status?.standaard_location_id ?? null, response_type: type, omschrijving: omschrijving.trim() || null }),
    onSuccess: () => {
      setOpen(false);
      setOmschrijving('');
      queryClient.invalidateQueries({ queryKey: ['medewerker-crisis'] });
    },
  });

  if (!status?.beschikbaar) return null;

  const gekozen = locatieId ?? status.standaard_location_id;
  const gekozenVestiging = status.vestigingen.find((v) => v.location_id === gekozen) ?? null;
  const mijnAlarm = status.mijn_alarmen[0] ?? null;

  const openSheet = () => {
    setLocatieId(status.standaard_location_id);
    setType('SINGLE');
    alarmMutation.reset();
    setOpen(true);
  };

  return (
    <>
      {mijnAlarm ? (
        <div className="rounded-xl border-2 border-red-300 bg-red-50 p-4" role="status" aria-live="polite">
          <div className="flex items-center gap-2 font-semibold text-red-900">
            <Siren className="w-5 h-5 text-red-600" aria-hidden="true" />
            {mijnAlarm.status === 'OPEN'
              ? t('crisis.zoekt', { defaultValue: 'Alarm verstuurd — we zoeken een crisisspecialist' })
              : t('crisis.onderweg', { defaultValue: 'Een crisisspecialist is onderweg' })}
          </div>
          {mijnAlarm.status === 'OPEN' && (
            <p className="mt-1 flex items-center gap-1.5 text-sm text-red-800">
              <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
              {t('crisis.cascade', { defaultValue: 'Eerst in je eigen regio, daarna landelijk.' })}
            </p>
          )}
          {mijnAlarm.status === 'ACCEPTED' && mijnAlarm.video_url && (
            <a
              href={mijnAlarm.video_url}
              target="_blank"
              rel="noreferrer"
              className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-[#0B1F3A] px-4 py-3 text-sm font-semibold text-white"
            >
              <Video className="w-4 h-4" aria-hidden="true" /> {t('crisis.video', { defaultValue: 'Open de videolink voor directe opvang' })}
            </a>
          )}
          <p className="mt-2 text-xs text-red-800">{t('crisis.112', { defaultValue: 'Direct levensgevaar? Bel altijd eerst 112.' })}</p>
        </div>
      ) : (
        <button
          type="button"
          onClick={openSheet}
          className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-red-600 hover:bg-red-700 active:bg-red-800 px-4 py-4 text-white text-base font-bold shadow-md"
        >
          <Siren className="w-6 h-6" aria-hidden="true" /> {t('crisis.knop', { defaultValue: 'CRISISALARM — acute hulp na een calamiteit' })}
        </button>
      )}

      {open && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50" role="dialog" aria-modal="true" aria-labelledby="crisis-titel">
          <div className="w-full sm:max-w-md max-h-[92vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl bg-white shadow-xl">
            <div className="flex items-center justify-between border-b px-4 py-3">
              <h2 id="crisis-titel" className="font-semibold text-gray-900 flex items-center gap-2">
                <Siren className="w-5 h-5 text-red-600" aria-hidden="true" /> {t('crisis.titel', { defaultValue: 'Crisisalarm afgeven' })}
              </h2>
              <button type="button" onClick={() => setOpen(false)} className="p-1 text-gray-400 hover:text-gray-600" aria-label={t('algemeen.sluiten', { defaultValue: 'Sluiten' })}>
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              <a href="tel:112" className="flex items-center gap-3 rounded-lg border-2 border-red-600 bg-white p-3 text-sm text-red-900">
                <Phone className="w-5 h-5 text-red-600 shrink-0" aria-hidden="true" />
                <span>
                  <strong>{t('crisis.112titel', { defaultValue: 'Levensgevaar, brand of letsel? Bel 112.' })}</strong>
                  <br />
                  {t('crisis.112uitleg', { defaultValue: 'Deze knop alarmeert de psychosociale crisisdienst van BeLagom voor opvang ná een schokkende gebeurtenis.' })}
                </span>
              </a>

              {status.vestigingen.length > 1 || !status.standaard_location_id ? (
                <label className="block text-sm">
                  <span className="block text-xs font-medium text-gray-700 mb-1">{t('crisis.vestiging', { defaultValue: 'Waar is het gebeurd?' })}</span>
                  <select value={gekozen ?? ''} onChange={(e) => setLocatieId(e.target.value || null)} className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm">
                    <option value="">{t('crisis.kiesVestiging', { defaultValue: 'Kies een vestiging' })}</option>
                    {status.vestigingen.map((v) => (
                      <option key={v.location_id} value={v.location_id} disabled={v.alarm_loopt}>
                        {v.naam}
                        {v.plaats ? ` (${v.plaats})` : ''}
                        {v.alarm_loopt ? ` — ${t('crisis.loopt', { defaultValue: 'alarm loopt al' })}` : ''}
                      </option>
                    ))}
                  </select>
                </label>
              ) : (
                gekozenVestiging && <p className="text-sm text-gray-700">{t('crisis.voorVestiging', { defaultValue: 'Vestiging: {{naam}}', naam: gekozenVestiging.naam })}</p>
              )}

              {gekozenVestiging?.alarm_loopt && (
                <p className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900">
                  {t('crisis.alGealarmeerd', { defaultValue: 'Voor deze vestiging loopt al een alarm. De crisisdienst is onderweg of wordt gezocht.' })}
                </p>
              )}

              <div className="grid grid-cols-2 gap-2">
                {(
                  [
                    { w: 'SINGLE', icon: User, titel: t('crisis.single', { defaultValue: '1 specialist' }), sub: t('crisis.singleSub', { defaultValue: 'Eén of enkele betrokkenen' }) },
                    { w: 'DUAL', icon: Users, titel: t('crisis.dual', { defaultValue: '2 specialisten' }), sub: t('crisis.dualSub', { defaultValue: 'Groot incident, veel betrokkenen' }) },
                  ] as const
                ).map((o) => {
                  const Icon = o.icon;
                  const actief = type === o.w;
                  return (
                    <button key={o.w} type="button" onClick={() => setType(o.w)} aria-pressed={actief} className={`rounded-lg border p-3 text-left ${actief ? 'border-red-500 bg-red-50' : 'border-gray-200'}`}>
                      <Icon className={`w-5 h-5 ${actief ? 'text-red-600' : 'text-gray-400'}`} aria-hidden="true" />
                      <div className="mt-1 text-sm font-medium text-gray-900">{o.titel}</div>
                      <div className="text-[11px] text-gray-500">{o.sub}</div>
                    </button>
                  );
                })}
              </div>

              <label className="block text-sm">
                <span className="block text-xs font-medium text-gray-700 mb-1">{t('crisis.wat', { defaultValue: 'Wat is er gebeurd? (optioneel)' })}</span>
                <textarea
                  value={omschrijving}
                  onChange={(e) => setOmschrijving(e.target.value)}
                  rows={3}
                  maxLength={1000}
                  placeholder={t('crisis.watPlaceholder', { defaultValue: 'Korte situatieschets, zonder namen of medische details' })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
              </label>

              {alarmMutation.isError && (
                <p className="text-sm text-red-600" role="alert">
                  {foutMelding(alarmMutation.error, t('crisis.fout', { defaultValue: 'Het alarm kon niet worden verstuurd. Bel bij twijfel 112.' }))}
                </p>
              )}

              <p className="text-[11px] text-gray-500">
                {t('crisis.privacy', { defaultValue: 'Je werkgever en de contactpersoon calamiteiten ontvangen direct een melding onder je pseudoniem.' })}
              </p>
            </div>

            <div className="flex gap-2 border-t px-4 py-3">
              <Button variant="outline" className="flex-1" onClick={() => setOpen(false)}>
                {t('algemeen.annuleren', { defaultValue: 'Annuleren' })}
              </Button>
              <Button variant="danger" className="flex-1" disabled={!gekozen || Boolean(gekozenVestiging?.alarm_loopt)} loading={alarmMutation.isPending} onClick={() => alarmMutation.mutate()}>
                🚨 {t('crisis.versturen', { defaultValue: 'Alarm versturen' })}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MedewerkerCrisisKnop;
